/**
 * HeaderCard - Sticky site header with logo, navigation, and CTA.
 */
import React, { useState, useEffect } from 'react';
import { assetUrl } from '../../lib/assets';

export interface NavItem {
  label: string;
  href: string;
  cta?: boolean;
}

export interface HeaderCardProps {
  siteName?: string;
  logoSrc?: string;
  logoPosition?: 'left' | 'right';
  nav?: NavItem[];
  className?: string;
}

const HeaderCard: React.FC<HeaderCardProps> = ({
  siteName = 'Kuiken Technologies LLC.',
  logoSrc,
  logoPosition = 'left',
  nav = [],
  className = '',
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`nav-card ${scrolled ? 'scrolled' : ''} ${className}`}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--color-primary)',
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        boxShadow: scrolled ? '0 4px 16px rgba(1,22,38,0.5)' : 'none',
        transition: 'box-shadow var(--transition-base)',
      }}
      role="banner"
    >
      <div className="layout-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '4rem', flexDirection: logoPosition === 'right' ? 'row-reverse' : 'row' }}>
        {/* Logo */}
        <a href="/" aria-label={`${siteName} home`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', textDecoration: 'none' }}>
          {logoSrc && (
            <img src={assetUrl(logoSrc)} alt={siteName} style={{ height: '32px', width: 'auto' }} />
          )}
          <span style={{ fontFamily: 'var(--font-family-heading)', fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-xl)', color: '#ffffff' }}>
            {siteName}
          </span>
        </a>

        {/* Desktop Nav */}
        <nav aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-6)' }}>
          {nav.map((item) =>
            item.cta ? (
              <a key={item.label} href={item.href} className="btn btn-primary btn-sm">
                {item.label}
              </a>
            ) : (
              <a
                key={item.label}
                href={item.href}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--font-size-sm)',
                  transition: 'color var(--transition-fast)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        {/* Mobile menu toggle */}
        <button
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--spacing-2)', color: '#ffffff' }}
          className="mobile-menu-btn"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.12)',
            backgroundColor: 'var(--color-primary)',
            padding: 'var(--spacing-4)',
          }}
        >
          <nav aria-label="Mobile navigation" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {nav.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={item.cta ? 'btn btn-primary' : ''}
                style={item.cta ? {} : { color: 'rgba(255,255,255,0.85)', textDecoration: 'none', padding: 'var(--spacing-2) 0', fontWeight: 'var(--font-weight-medium)' }}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default HeaderCard;
